import { MouseEvent, useEffect, useState, memo, useMemo } from "react";
import UserActivityInfo from "../../common/UserActivityInfo";
import { useSearchParams } from "react-router-dom";
import styles from "./UserActInfo.module.css";
import { CategoryOptions, MapUserActivity } from "../../common/Common";
import DataProvider from "../../../data_provider/DataProvider";

import { useModal } from "../../modal/Modal";
import ModalButtons from "../../modal/ModalButtons";

import LoginForm from "../../login/LoginForm";
import SignUpModal from "../../login/SignUpModal";
import { useLoginStatus } from "../../../hooks/useLoginStatus";

interface UserActInfoProps {
    data: MapUserActivity;
    setIsBookmark?: (value: boolean) => void;
    overlapMapInfos?: OverlapMapInfo[];
    selectedMapIdx: number;
    onBookmarkChange?: (isBookmark: boolean) => void;
}

export interface OverlapMapInfo {
    bookmark: boolean | undefined;
    mapId: string;
    title: string;
    category: CategoryOptions[];
    viewCnt: number;
    bookmarkCnt: number;
    thumbCnt: number;
}

interface UserActivity {
    count: number;
    label?: string;
    type?: string;
    column?: boolean;
    size?: string;
    iconStyle?: { color?: string; cursor?: string };
    labelStyle?: { [key: string]: string };
    onClickIcon?: (e: MouseEvent<HTMLElement>) => void;
}

/**
 * @author L.chanwoo
 * @date 2024-10-15 15:47:33
 * @remark 사이드레일 하단 지도 유저참여활동 정보
 * @returns
 */
const UserActInfo = ({ data, overlapMapInfos, selectedMapIdx, onBookmarkChange }: UserActInfoProps) => {
    const { ModalComponent, openModal, closeModal } = useModal();
    const isLogin = useLoginStatus(); // login검증 커스텀 훅 이용 | smyun | 250723

    const [params] = useSearchParams();
    // const [userAct, setUserAct] = useState<UserActivity[]>([]);

    // useEffect(() => {
    //     setUserActivityCnt();
    // }, [data, data?.bookmark, data?.bookmarkCnt, selectedMapIdx, isLogin]); // 옵셔널 체이닝 추가하고 selectedMapIdx도 감지

    // const setUserActivityCnt = () => {
    //     if (data) {
    //         const labelStyle = { fontSize: "0.7rem", color: "grey" };
    //         const bookmarkIconColor = data.bookmark ? "default" : "lightGrey";

    //         const mapUserActInfo = [
    //             {
    //                 count: data.bookmarkCnt,
    //                 type: "bookmark",
    //                 label: "즐겨찾기",
    //                 column: true,
    //                 onClickIcon: onClickBookmark,
    //                 labelStyle: labelStyle,
    //                 iconStyle: {
    //                     color: bookmarkIconColor,
    //                 },
    //             },
    //             {
    //                 count: data.viewCnt,
    //                 type: "view",
    //                 label: "조회수",
    //                 column: true,
    //                 labelStyle: labelStyle,
    //                 iconStyle: { color: "default" },
    //             },

    //             {
    //                 count: data.thumbCnt,
    //                 type: "thumbUp",
    //                 label: "도움돼요",
    //                 column: true,
    //                 labelStyle: labelStyle,
    //                 iconStyle: { color: "default" },
    //             },
    //         ];
    //         setUserAct(mapUserActInfo);
    //     } else {
    //         console.warn("data가 없습니다!");
    //     }
    // };

    // 25.09.04 | Kim Young Jun | 북마크 클릭 이벤트 핸들러 수정
    const onClickBookmark = (e: MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (!isLogin) {
            // 로그인 모달 오픈
            openModal({
                title: "안내",
                children: (
                    <>
                        로그인이 필요한 서비스입니다.
                        <br />
                        로그인을 하시겠습니까?
                    </>
                ),
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "확인",
                                color: "Primary",
                                onClick: () =>
                                    openModal({
                                        title: "로그인",
                                        children: <LoginForm onClose={() => closeModal()} onClickSignUp={onClickSignUp} />,
                                        modalButtons: null,
                                        size: "wide",
                                        disableOverlayClose: true,
                                    }),
                            },
                        ]}
                    />
                ),
            });
            return;
        }

        const newBookmark = !data.bookmark;

        // 로그인 상태면 북마크 업데이트 진행
        proceedBookmark(newBookmark);
    };

    // 실제 즐겨찾기 수행을 함수로 분리
    const proceedBookmark = (nextVal: boolean) => {
        // 화면 반영
        onBookmarkChange?.(nextVal);
        // API 호출
        fetchBookmarkCnt(nextVal);
    };

    // userAct 클로저(bookmark 값)가 stale됨 => 매 렌더에 계산할 수 있게 useState에서 useMemo로 변경 | smyun | 250906
    const userAct = useMemo<UserActivity[]>(() => {
        if (!data) return [];
        const labelStyle = { fontSize: "0.7rem", color: "grey" };
        const bookmarkIconColor = isLogin ? (data.bookmark ? "default" : "lightGrey") : "lightGrey";

        return [
            {
                count: data.bookmarkCnt,
                type: "bookmark",
                label: "즐겨찾기",
                column: true,
                onClickIcon: onClickBookmark, // 최신 클로저를 참조
                labelStyle,
                iconStyle: { color: bookmarkIconColor },
            },
            {
                count: data.viewCnt,
                type: "view",
                label: "조회수",
                column: true,
                labelStyle,
                iconStyle: { color: "default" },
            },
            {
                count: data.thumbCnt,
                type: "thumbUp",
                label: "도움돼요",
                column: true,
                labelStyle,
                iconStyle: { color: "default" },
            },
        ];
    }, [data, isLogin]);

    // 회원가입
    const onClickSignUp = () => {
        openModal({
            title: "회원가입",
            children: <SignUpModal onClose={() => closeModal()} />,
            modalButtons: null,
            size: "normal",
        });
    };

    // 25.08.13 | Kim Young Jun | API 호출 책임만 지니도록 부분 수정
    const fetchBookmarkCnt = async (newBookmark: boolean) => {
        try {
            const mapId = overlapMapInfos ? overlapMapInfos[selectedMapIdx].mapId : params.get("id");
            const response = await DataProvider.UpdateUserActivity({ mapid: mapId! });

            if (response.flag) {
                console.log("북마크 업데이트 성공");
                // 성공 시에는 현재 상태 유지 (이미 업데이트됨)
            } else {
                console.error("북마크 업데이트 실패");
                // 실패 시 롤백
                if (onBookmarkChange) {
                    console.log("실패로 인한 롤백 중...");
                    onBookmarkChange(!newBookmark);
                }

                if (response.status === 403) {
                    // TODO: 에러 팝업/모달 처리
                }
            }
        } catch (error) {
            console.error("북마크 처리 중 오류:", error);
            // 에러 발생 시 롤백
            if (onBookmarkChange) {
                console.log("오류로 인한 롤백 중...");
                onBookmarkChange(!newBookmark);
            }
        }
    };

    // const renderItem = () => {
    //     if (userAct) {
    //         const button = userAct.map((item, index) => {
    //             return <UserActivityInfo key={index} {...item} />;
    //         });
    //         return button;
    //     }
    // };

    const renderItem = () => userAct.map((item, idx) => <UserActivityInfo key={idx} {...item} />);

    return (
        <>
            <ModalComponent />
            <div className={styles.wrapper}>{renderItem()}</div>
        </>
    );
};

export default memo(UserActInfo);
